import { Component, Input } from "@angular/core";
import {
  CHANGE_HISTORY_LABELS,
  ChangeHistoryEntry,
} from "./change-history-entry";

// CO-2315 Round-2 item #11 - the shared field-change history panel.
//
// The markup and behaviour are LIFTED from the panel that already ships on
// pages/company/facilities-view (addChangesDetails + facilities-view.html), which has
// rendered facility rate changes on the screen that owns them since before this ticket. That
// panel is the reason D-1 chose the owning screen: the pattern is not a proposal, it is
// production code, and R&B F1.S13's ruling to the Activity Log Report page was already
// contradicted by it.
//
// This component is DELIBERATELY PRESENTATIONAL. Sorting and paging stay with the host.
// facilities-view keeps its own addChangesDetails and changesOnPageChange untouched, so its
// existing specs pass unedited - the guard that the extraction changed no live behaviour. New
// hosts use sortNewestFirst/pageEntries below rather than reimplementing them.
@Component({
  selector: "change-history-panel",
  templateUrl: "change-history-panel.html",
})
export class ChangeHistoryPanelComponent {
  labels = CHANGE_HISTORY_LABELS;

  // The rows to render - ONE PAGE of them. The host pages; see pageEntries.
  @Input() entries: ChangeHistoryEntry[] = [];

  // Pager wiring, passed straight through to the shared grid-pager.
  @Input() showPager: boolean = false;
  @Input() totalPages: number = 0;
  @Input() currentPage: number = 0;

  // Rendered when there is nothing to show. An empty table with headers and no rows reads
  // as a broken bind; a sentence reads as an answer.
  @Input() emptyMessage: string = CHANGE_HISTORY_LABELS.empty;

  // Set false to drop the section heading when the host supplies its own.
  @Input() showHeading: boolean = true;

  @Input() pageChanged: (page: number) => void = () => {};

  get hasEntries(): boolean {
    return !!this.entries && this.entries.length > 0;
  }

  // authorOf renders the actor. Falls back to whatever single name is present rather than
  // to " " - the donor template interpolates `first + " " + last` directly, which renders a
  // lone space when both are absent, and a blank author cell reads as a system change.
  authorOf(entry: ChangeHistoryEntry): string {
    if (!entry) {
      return "";
    }
    const first = (entry.userFirstName || "").trim();
    const last = (entry.userLastName || "").trim();
    return (first + " " + last).trim();
  }

  // newValueOf appends a marker when the value was reconstructed rather than recorded.
  //
  // This is the honesty rule, and it is the whole reason the panel renders new values at
  // all rather than refusing to. A derived value is a good guess: the audit's write path is
  // best-effort, so a change can have been made and never recorded, and BenefitsVersion
  // cannot bound the gap. Presenting a guess as a record would be worse than showing
  // nothing.
  newValueOf(entry: ChangeHistoryEntry): string {
    if (!entry) {
      return "";
    }
    const value = entry.newValue || "";
    if (value !== "" && entry.newValueDerived) {
      return value + " " + CHANGE_HISTORY_LABELS.derivedSuffix;
    }
    return value;
  }

  onPageChanged(page: number): void {
    if (this.pageChanged) {
      this.pageChanged(page);
    }
  }
}

// sortNewestFirst orders entries by changeDate descending, copying the donor's comparator
// (facilities-view.ts addChangesDetails). It does not mutate its argument - the donor sorts
// in place on a field of the object it was handed, which is fine there and a trap anywhere
// a caller reuses the array.
export function sortNewestFirst(
  entries: ChangeHistoryEntry[]
): ChangeHistoryEntry[] {
  return (entries || [])
    .slice()
    .sort(
      (a, b) =>
        new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime()
    );
}

// pageEntries splits sorted entries into pages of pageSize.
export function pageEntries(
  entries: ChangeHistoryEntry[],
  pageSize: number
): ChangeHistoryEntry[][] {
  const size = pageSize > 0 ? pageSize : 10;
  const sorted = sortNewestFirst(entries);
  const pages: ChangeHistoryEntry[][] = [];
  for (let i = 0; i < sorted.length; i += size) {
    pages.push(sorted.slice(i, i + size));
  }
  return pages;
}



// WEBPACK FOOTER //
// ./src/components/change-history-panel/change-history-panel.ts